import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { getCurrentUser, handleOAuthCallback, signOut as doSignOut, type AuthUser } from './auth-service';

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue>({ user: null, loading: true, signOut: () => {} });

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');

    if (code) {
      handleOAuthCallback(code)
        .then(() => {
          window.history.replaceState({}, '', window.location.pathname);
          return getCurrentUser();
        })
        .then(setUser)
        .finally(() => setLoading(false));
    } else {
      getCurrentUser()
        .then(setUser)
        .finally(() => setLoading(false));
    }
  }, []);

  function signOut() {
    doSignOut();
    setUser(null);
    window.location.href = '/';
  }

  return (
    <AuthContext.Provider value={{ user, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
